import { useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  Button,
  InpockInput,
  Paragraph1,
  Paragraph2,
  Rules,
  TextArea,
  ToggleButton,
} from 'inpock-ui-react';
import styled from '@emotion/styled';

import { Accordion, Dialog, PostcodeDialog, PostcodePage } from '@/components';
import {
  CUSTOMER_ADDRESS,
  CUSTOMER_ADDRESS_DETAIL,
  CUSTOMER_NAME,
  CUSTOMER_PHONE,
  CUSTOMER_ZIPCODE,
} from '@/constants';
import { api, ShippingType } from '@/apis';
import { useDialog, useForm } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { useSectionContext } from '@/components/checkout/SectionContext';
import { ORDER_TEMPLATE } from '@/utils';

import { AddressInfo, BuyerInfo, FormProps } from '../types';

interface AddressInfoFormProps extends FormProps<AddressInfo> {
  buyerInfo: BuyerInfo;
  shippingType: string;
  setShippingType: (type: ShippingType) => void;
}

const AddressSectionContainer = styled.section`
  .test {
    margin-bottom: 14px !important;
  }
`;

export function AddressSection(props: AddressInfoFormProps) {
  const { addressSectionRef } = useSectionContext();
  const [dialogOn] = useDialog();
  const [loading] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [hasSameAsBuyer, setHasSameAsBuyer] = useState<boolean>(false);
  const { loggedIn } = useAuth();
  const [backdropElement, setBackdropElement] = useState<HTMLDivElement | null>(null);

  const handleRequestPostCodePage = () => setSearching(true);

  const { form: addressForm, onInput, inputRef, register } = useForm(props.form);

  useImperativeHandle(addressSectionRef, () => ({
    ...inputRef.current,
  }));

  useEffect(() => {
    Object.entries(props.form).map(([k, v]) => {
      onInput(v, k);
    });
  }, [onInput, props.form]);

  const headerContent = () => {
    if (addressForm.receiver_address !== '' || addressForm.receiver_detail_address !== '') {
      return `${addressForm.receiver_address}, ${addressForm.receiver_detail_address}`;
    } else {
      return '-';
    }
  };

  const onClickHasSameAsBuyer = () => {
    setHasSameAsBuyer((v) => !v);

    if (!hasSameAsBuyer) {
      onInput(props.buyerInfo.buyer_name, 'receiver_name');
      onInput(props.buyerInfo.buyer_phone, 'receiver_phone');

      props.setForm((prev) => {
        return {
          ...prev,
          receiver_name: props.buyerInfo.buyer_name,
          receiver_phone: props.buyerInfo.buyer_phone,
        };
      });
    } else {
      onInput('', 'receiver_name');
      onInput('', 'receiver_phone');

      props.setForm((prev) => {
        return {
          ...prev,
          receiver_name: '',
          receiver_phone: '',
        };
      });
    }
  };

  // 회원일 시에 주문자 정보 자동입력
  useEffect(() => {
    if (loggedIn) {
      const newBuyForm = {
        receiver_name: props.customer?.receiver_name ?? '',
        receiver_phone: props.customer?.receiver_phone ?? '',
        receiver_postcode: props.customer?.receiver_postcode ?? '',
        receiver_address: props.customer?.receiver_address ?? '',
        receiver_detail_address: props.customer?.receiver_detail_address ?? '',
        delivery_message: props.customer?.delivery_message ?? '',
      };

      Object.entries(newBuyForm).map(([k, v]) => {
        onInput(v as string, k);
      });

      props.setForm(newBuyForm);
    }
  }, [loggedIn, props.customer]);

  useEffect(() => {
    if (!hasSameAsBuyer) return;
    onInput(props.buyerInfo.buyer_name, 'receiver_name');
    onInput(props.buyerInfo.buyer_phone, 'receiver_phone');

    props.setForm((prev) => {
      return {
        ...prev,
        receiver_name: props.buyerInfo.buyer_name,
        receiver_phone: props.buyerInfo.buyer_phone,
      };
    });
  }, [props.buyerInfo]);

  useEffect(() => {
    setBackdropElement(document.getElementById('backdrop') as HTMLDivElement);
  }, []);

  return (
    <>
      {backdropElement &&
        searching &&
        createPortal(
          <PostcodeDialog onHide={setSearching}>
            <PostcodePage
              onComplete={async (result) => {
                const { postcode, address } = result;
                onInput(postcode, 'receiver_postcode');
                onInput(address, 'receiver_address');
                props.setForm((prev) => {
                  return { ...prev, receiver_postcode: postcode, receiver_address: address };
                });
                setSearching(false);
                const { shipping_type } = await api.orders.getShippingType(postcode);
                props.setShippingType(shipping_type);
                if (shipping_type === 'remote' || shipping_type === 'jeju') {
                  return dialogOn({
                    template: ORDER_TEMPLATE.shippingType.new(shipping_type as 'remote' | 'jeju'),
                    close: {
                      text: '확인',
                    },
                  });
                } else {
                  const oldShippingType = props.shippingType;
                  if (oldShippingType && oldShippingType !== shipping_type) {
                    return dialogOn({
                      template: ORDER_TEMPLATE.shippingType.change(),
                      close: {
                        text: '확인',
                      },
                    });
                  }
                }
              }}
            />
          </PostcodeDialog>,
          backdropElement,
        )}
      {searching && (
        <Dialog onHide={setSearching}>
          <PostcodePage
            onComplete={(result) => {
              const { postcode, address } = result;
              onInput(postcode, 'receiver_postcode');
              onInput(address, 'receiver_address');
              props.setForm((prev) => {
                return { ...prev, receiver_postcode: postcode, receiver_address: address };
              });
              setSearching(false);
            }}
          />
        </Dialog>
      )}
      <Accordion
        open={props.isOpen}
        firstRow={<Paragraph1 fontWeight="bold">배송 정보</Paragraph1>}
        secondRow={<Paragraph2 color="gray500">{headerContent()}</Paragraph2>}
      >
        <AddressSectionContainer className="mt-24 mb-30">
          <InpockInput
            ref={(el) => (inputRef.current['receiver_name'] = el)}
            value={addressForm.receiver_name}
            {...register('receiver_name')}
            onInput={(input_value, _) => {
              onInput(input_value, 'receiver_name');
              props.setForm((prev) => {
                return { ...prev, receiver_name: input_value };
              });
            }}
            type="text"
            guide="받는 사람"
            required
            width="100%"
            disabled={loading !== false}
            maxLength={CUSTOMER_NAME.rules?.max_length}
            validator={(_rules, value) =>
              CUSTOMER_NAME.validator &&
              CUSTOMER_NAME.validator(CUSTOMER_NAME.rules as Rules, value)
            }
            rules={CUSTOMER_NAME.rules}
            postfix={
              props.customer?.receiver_name && props.customer?.receiver_phone ? undefined : (
                <ToggleButton size="medium" onClick={onClickHasSameAsBuyer} value={hasSameAsBuyer}>
                  구매자와 동일
                </ToggleButton>
              )
            }
          />
          <InpockInput
            ref={(el) => (inputRef.current['receiver_phone'] = el)}
            value={addressForm.receiver_phone}
            {...register('receiver_phone')}
            onInput={(input_value, _) => {
              onInput(input_value, 'receiver_phone');
              props.setForm((prev) => {
                return { ...prev, receiver_phone: input_value };
              });
            }}
            type="text"
            inputMode="numeric"
            guide="연락처"
            placeholder="01000000000"
            required
            width="100%"
            maxLength={CUSTOMER_PHONE.rules?.max_length}
            validator={(_rules, value) =>
              CUSTOMER_PHONE.validator &&
              CUSTOMER_PHONE.validator(CUSTOMER_PHONE.rules as Rules, value)
            }
            rules={CUSTOMER_PHONE.rules}
          />
          <InpockInput
            ref={(el) => (inputRef.current['receiver_postcode'] = el)}
            value={addressForm.receiver_postcode}
            onClick={handleRequestPostCodePage}
            onInput={(input_value, _) =>
              props.setForm((prev) => {
                return { ...prev, receiver_postcode: input_value };
              })
            }
            type="text"
            guide="배송지"
            placeholder="우편번호"
            required
            width="100%"
            disabled={true}
            maxLength={CUSTOMER_ZIPCODE.rules?.max_length}
            validator={(_rules, value) =>
              CUSTOMER_ZIPCODE.validator &&
              CUSTOMER_ZIPCODE.validator(CUSTOMER_ZIPCODE.rules as Rules, value)
            }
            rules={CUSTOMER_ZIPCODE.rules}
            style={{ marginBottom: 10 }}
            clearable={false}
            postfix={
              <Button size="medium" type="primary" onClick={handleRequestPostCodePage}>
                우편번호 검색
              </Button>
            }
          />
          <InpockInput
            ref={(el) => (inputRef.current['receiver_address'] = el)}
            value={addressForm.receiver_address}
            {...register('receiver_address')}
            onClick={handleRequestPostCodePage}
            onInput={() => {}}
            type="text"
            placeholder="배송지"
            width="100%"
            disabled={true}
            maxLength={CUSTOMER_ADDRESS.rules?.max_length}
            validator={(_rules, value) =>
              CUSTOMER_ADDRESS.validator &&
              CUSTOMER_ADDRESS.validator(CUSTOMER_ADDRESS.rules as Rules, value)
            }
            rules={CUSTOMER_ADDRESS.rules}
            style={{ marginBottom: 10 }}
            clearable={false}
          />
          <InpockInput
            ref={(el) => (inputRef.current['receiver_detail_address'] = el)}
            value={addressForm.receiver_detail_address}
            {...register('receiver_detail_address')}
            className="test"
            onInput={(input_value, _) =>
              props.setForm((prev) => {
                onInput(input_value, 'receiver_detail_address');
                return { ...prev, receiver_detail_address: input_value };
              })
            }
            onClick={() => handleRequestPostCodePage}
            type="text"
            placeholder="상세 주소"
            width="100%"
            disabled={loading !== false}
            maxLength={CUSTOMER_ADDRESS_DETAIL.rules?.max_length}
            validator={(_rules, value) =>
              CUSTOMER_ADDRESS_DETAIL.validator &&
              CUSTOMER_ADDRESS_DETAIL.validator(CUSTOMER_ADDRESS_DETAIL.rules as Rules, value)
            }
            rules={CUSTOMER_ADDRESS_DETAIL.rules}
          />
        </AddressSectionContainer>
      </Accordion>
      <div style={{ margin: '16px 12px' }}>
        <TextArea
          width="100%"
          guide="배송 메시지"
          placeholder="부재 시 문 앞에 놓아 주세요."
          resize="vertical"
          value={addressForm.delivery_message}
          onInput={(input_value, _) =>
            props.setForm((prev) => {
              onInput(input_value, 'delivery_message');
              return { ...prev, delivery_message: input_value };
            })
          }
        />
      </div>
    </>
  );
}
